// src/components/common/ActivityMarquee.jsx
// The "right now" activity ticker. Lifted out of Home.jsx so the signed-in Home
// can show the same strip without a second copy of the fetch-and-fall-back logic.
// Home.jsx still has its own inline version; it can adopt this later.
//
// /api/activity-feed does not exist yet. Until it does this silently stays on
// FALLBACK_ACTIVITY rather than rendering an error strip.
import { useEffect, useState } from 'react';
import { Box } from '@mui/material';
import { keyframes } from '@mui/material/styles';
import { CallSplit, CheckCircle, MenuBook } from '@mui/icons-material';

const ACTIVITY_ICONS = { branch: CallSplit, check: CheckCircle, book: MenuBook };

const FALLBACK_ACTIVITY = [
  { type: 'branch', text: 'New idea branch on "The Long Ledger"' },
  { type: 'check', text: 'Reviewer accepted a revision on "Notes on Attention"' },
  { type: 'book', text: '"A Quiet Method" just published' },
];

// Translates the full track by -50%. The items are rendered twice so the seam
// lands back at the start and the loop reads as continuous.
const marqueeScroll = keyframes`
  0% { transform: translateX(0); }
  100% { transform: translateX(-50%); }
`;

export default function ActivityMarquee({ sx }) {
  const [activity, setActivity] = useState(FALLBACK_ACTIVITY);

  useEffect(() => {
    let cancelled = false;
    async function refreshActivity() {
      try {
        const res = await fetch('/api/activity-feed');
        if (!res.ok) throw new Error('activity feed endpoint not available yet');
        const data = await res.json();
        if (!cancelled && Array.isArray(data) && data.length) setActivity(data);
      } catch {
        // stay on FALLBACK_ACTIVITY
      }
    }
    refreshActivity();
    const id = setInterval(refreshActivity, 60 * 60 * 1000); // hourly
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  return (
    <Box
      data-testid="home-activity-marquee"
      sx={{ bgcolor: 'surface.ink', borderRadius: 2.5, overflow: 'hidden', py: 1.5, ...sx }}
    >
      <Box
        sx={{
          display: 'flex',
          width: 'max-content',
          whiteSpace: 'nowrap',
          animation: `${marqueeScroll} 20s linear infinite`,
        }}
      >
        {[...activity, ...activity].map((item, i) => {
          const Icon = ACTIVITY_ICONS[item.type] ?? CallSplit;
          return (
            <Box
              key={i}
              component="span"
              sx={{
                color: 'common.white',
                fontSize: 12,
                mr: 6,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.75,
              }}
            >
              <Icon sx={{ fontSize: 13, color: 'secondary.main' }} />
              {item.text}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}
